import type { Translation } from '../../types/i18n'

export const fr: Translation = {
  announcement:
    'Faits sur commande · 3 jours d’avance · Minimum 50 $ · Cueillette à Chambly ou livraison',
  nav: {
    home: 'Accueil',
    cacao: 'L’Art du Cacao',
    creations: 'Nos Créations',
    ingredients: 'Ingrédients',
    giftCard: 'Carte-cadeau',
    recipes: 'Recettes',
    events: 'Corporatif & Événements',
    contact: 'Nous Joindre',
    cart: 'Panier',
    order: 'Commander',
  },
  hero: {
    eyebrow: 'Chocolaterie artisanale · Chambly',
    titleLead: 'Les Artisans Sucrés',
    titleAccent: 'L’Art du Cacao',
    subtitle:
      'Un cacao de caractère, des créations élégantes et une histoire de résilience et de passion.',
    ctaPrimary: 'Découvrir nos créations',
    ctaSecondary: 'L’Art du Cacao',
  },
  cacao: {
    eyebrow: 'Notre philosophie',
    titleLead: 'L’Art du',
    titleAccent: 'Cacao',
    intro:
      'Le cacao est bien plus qu’un ingrédient : c’est un héritage vivant, un fruit sacré que les civilisations anciennes considéraient comme un trésor. Chaque fève porte une histoire, une énergie et une noblesse que nous honorons avec respect et délicatesse.',
    benefitsTitle: 'Avant de devenir chocolat, le cacao est un aliment puissant',
    benefits: [
      'Riche en antioxydants',
      'Bénéfique pour le cœur',
      'Élévateur naturel de l’humeur',
      'Source de minéraux essentiels',
      'Favorise la mémoire et la concentration',
    ],
    philosophyTitle: 'Notre credo',
    philosophyQuote: 'Moins de sucre, plus de cacao.',
    philosophyText:
      'Plus le pourcentage de cacao est élevé, plus le chocolat est authentique, nourrissant et rempli de vraie saveur. Nos créations sont pensées pour être saines, raffinées et profondément vraies.',
    heritageTitle: 'Un hommage à nos racines',
    heritageText:
      'Les alfajores péruviens — classiques ou enrobés de chocolat de qualité — sont un hommage à ses racines. Une alliance entre tradition et modernité, façonnée à la main, avec intention, précision et amour.',
  },
  promo: {
    badge: 'Offre de bienvenue',
    title:
      'Inscrivez-vous et obtenez 15 % de rabais sur votre première commande en ligne.',
    text: 'Et débloquez plus de rabais, de recettes et de douceurs chocolatées exclusives.',
    placeholder: 'Votre adresse courriel',
    cta: 'Je m’inscris',
    success: 'Merci ! Votre code de 15 % arrive par courriel.',
  },
  story: {
    eyebrow: 'Mon histoire',
    titleLead: 'Une passion pour le',
    titleAccent: 'vrai chocolat',
    strugglesIntro: 'Mes débuts n’ont pas été faciles :',
    struggles: [
      'Le chocolat refusait de se tempérer',
      'Les moules ne se détachaient pas',
      'Les colorants restaient collés',
    ],
    paragraphs: [
      'Mais je ne me suis jamais arrêtée. J’ai pratiqué, échoué, recommencé… encore et encore. Ma philosophie est claire : aucun obstacle ne peut arrêter la passion, la discipline et le désir d’atteindre la perfection.',
      'Aujourd’hui, je crée des chocolats uniques, élégants et remplis d’émotion. Chez Les Artisans Sucrés, nous travaillons des chocolats à pourcentage élevé pour offrir des produits plus sains, plus authentiques et riches en saveur réelle.',
      'Nous proposons également nos alfajores péruviens classiques et enrobés de chocolat de qualité, un hommage à mes racines, revisité avec une touche moderne et raffinée.',
      'Les Artisans Sucrés est l’expression de mon histoire, de ma résilience et de mon amour profond pour l’art du cacao.',
    ],
    closing:
      'Chaque produit est façonné à la main, avec intention, précision et amour.',
    signatureName: 'Sara',
    signatureRole: 'Maître Chocolatière',
  },
  products: {
    eyebrow: 'Nos Collections',
    titleLead: 'Créations',
    titleAccent: 'Gourmandes',
    addToCart: 'Ajouter au panier',
    added: 'Ajouté !',
    quantity: 'Quantité',
    chooseOption: 'Choisissez votre format',
    saveForLater: 'Garder pour plus tard',
    savedLabel: 'Gardé',
    orderNow: 'Passer la commande',
    from: 'dès',
    categoriesLabel: 'Catégories',
    categories: [
      { id: 'all', name: 'Tout voir' },
      { id: 'alfajores', name: 'Alfajores' },
      { id: 'chocolats', name: 'Chocolats fins' },
      { id: 'tablettes', name: 'Tablettes' },
      { id: 'croustillants', name: 'Croustillants' },
      { id: 'cakepops', name: 'Cake pops' },
    ],
    items: [
      {
        id: 'alfajores',
        name: 'Alfajores Péruviens',
        subtitle: 'Notre Signature',
        description:
          'Deux biscuits fondants enlaçant un cœur généreux de dulce de leche artisanal et de confiture de fraise maison, saupoudrés de sucre glace. Un hommage à mes racines péruviennes, en boîtes de 12 à 60.',
        tags: ['Dulce de leche', 'Confiture de fraise', '12 à 60'],
      },
      {
        id: 'alfajores-enrobes',
        name: 'Alfajores Enrobés de Chocolat',
        subtitle: 'Tradition & Cacao',
        description:
          'Nos alfajores péruviens entièrement enrobés de chocolat de qualité — noir 70 %, blanc ou au lait — et garnis d’amandes, de pacanes ou de canneberges. La boîte de 12.',
        tags: ['Noir 70 %', 'Blanc', 'Au lait'],
      },
      {
        id: 'bonbons',
        name: 'Chocolats Pralinés',
        subtitle: 'L’Élégance du Cacao',
        description:
          'Bonbons pralinés peints à la main, à la coque fine et au cœur soyeux. Emballés un à un, ils se déclinent en boîtes de 12, 24 et 36 chocolats.',
        tags: ['Praliné', 'Peint à la main', '12 · 24 · 36'],
      },
      {
        id: 'pralines',
        name: 'Pralines Maison aux Noix',
        subtitle: 'Fait Maison',
        description:
          'Nos pralines faites maison à base de noix : amandes, noisettes et raisins dorés sur un disque de chocolat blanc, au lait ou noir. Vendues en paquet de 4.',
        tags: ['Amandes', 'Noisettes', '5 $ le paquet de 4'],
      },
      {
        id: 'fruits',
        name: 'Chocolats aux Fruits',
        subtitle: 'Fruit de la passion · Lucuma · Fraise · Baileys',
        description:
          'Des bonbons au cœur coulant de fruit de la passion, de lucuma, de fraise ou de Baileys, à la coque marbrée peinte à la main. Nous travaillons avec des ingrédients frais à 100 %, sans arômes artificiels.',
        tags: ['Fruit de la passion', 'Lucuma', 'Fraise', 'Baileys'],
      },
      {
        id: 'tablettes',
        name: 'Tablettes de Chocolat',
        subtitle: 'Multiples Saveurs',
        description:
          'Nos tablettes sont disponibles en multiples saveurs : chocolat blanc, chocolat au lait et chocolat noir 70 %, garnies de fruits séchés, de noix ou d’écorces confites.',
        tags: ['Blanc', 'Lait', 'Noir 70 %'],
      },
      {
        id: 'pretzels',
        name: 'Pretzels Enrobés de Chocolat',
        subtitle: 'Sucré-Salé',
        description:
          'Des pretzels enrobés de chocolat au lait et de chocolat noir 70 % : une amalgame de croustillant, de sucré et de salé, impossible à arrêter après la première bouchée.',
        tags: ['Croustillant', 'Sucré-salé', '10 unités · 13 $'],
      },
      {
        id: 'cakepops',
        name: 'Cake Pops',
        subtitle: 'Douceur Festive',
        description:
          'Des bouchées de gâteau moelleux enrobées de chocolat brillant et décorées à la main. Assortis chocolat et vanille, vendus à l’unité.',
        tags: ['Assortis', 'Chocolat & vanille', '3 $ l’unité'],
      },
    ],
  },
  ingredients: {
    eyebrow: 'Nos ingrédients',
    titleLead: 'La qualité, avant',
    titleAccent: 'tout le reste',
    intro:
      'Un chocolat n’est jamais meilleur que ses ingrédients. Nous choisissons chaque composant pour son goût réel, jamais pour sa facilité.',
    freshNote: 'Nous travaillons avec des ingrédients frais à 100 %.',
    items: [
      {
        title: 'Pastilles de cacao Cacao Barry',
        text:
          'Nous travaillons exclusivement avec les pastilles de couverture Cacao Barry, une référence française reconnue pour la pureté de ses fèves et la finesse de sa fluidité en tempérage.',
      },
      {
        title: 'Haut pourcentage de cacao',
        text:
          'Nos chocolats privilégient un pourcentage de cacao élevé : moins de sucre, plus de vraie saveur et un profil aromatique franc.',
      },
      {
        title: 'Fruits, noix et beurre pur',
        text:
          'Amandes, noisettes, pacanes, canneberges, fruits frais et beurre pur — aucun arôme artificiel, aucun substitut de gras.',
      },
    ],
  },
  giftCard: {
    eyebrow: 'Carte-cadeau',
    titleLead: 'Offrez la',
    titleAccent: 'gourmandise',
    intro:
      'Vous ne savez pas quoi choisir ? Offrez une carte-cadeau Les Artisans Sucrés, valable sur toutes nos créations, en cueillette comme en livraison.',
    amountsLabel: 'Montants offerts',
    cta: 'Commander une carte-cadeau',
    note:
      'Les cartes-cadeaux sont envoyées par courriel et n’expirent pas. Écrivez-nous en message privé ou par courriel pour la personnaliser.',
  },
  orderInfo: {
    eyebrow: 'Comment commander',
    titleLead: 'Frais, faits',
    titleAccent: 'sur commande',
    intro:
      'Nous ne gardons aucun stock : chaque boîte est préparée spécialement pour vous, après réception de votre commande.',
    cards: [
      {
        title: 'Sur commande · 3 jours d’avance',
        text:
          'Tous nos produits sont faits sur commande. Prévoyez un délai minimum de 3 jours avant la date souhaitée afin que nous puissions tout préparer à la main.',
      },
      {
        title: 'Commande minimum de 50 $',
        text:
          'Un montant minimum de 50 $ est requis pour toute commande, en cueillette comme en livraison.',
      },
      {
        title: 'Commande par DM',
        text:
          'Pour les événements et les cadeaux corporatifs, écrivez-nous directement en message privé sur Instagram, Facebook ou TikTok.',
      },
    ],
    pickupTitle: 'Cueillette à Chambly',
    deliveryTitle: 'Livraison',
    deliveryNote:
      'Les frais de livraison sont calculés selon la distance. Contactez-nous pour le tarif exact.',
    dmTitle: 'Commande par message privé',
    dmText:
      'Événements, mariages et cadeaux corporatifs : écrivez-nous en DM et nous préparons votre projet sur mesure.',
  },
  recipes: {
    eyebrow: 'Notre atelier chez vous',
    titleLead: 'Recettes',
    titleAccent: 'Gourmandes',
    intro:
      'Quelques-unes de nos recettes préférées à réaliser en famille. Prenez votre temps : la patience est le premier ingrédient de l’artisanat.',
    ingredientsLabel: 'Ingrédients',
    stepsLabel: 'Préparation',
    items: [
      {
        id: 'dulce',
        title: 'Dulce de Leche Maison',
        time: '2 h 30',
        yield: 'environ 400 g',
        description:
          'Le cœur de nos alfajores. Un caramel de lait lent, onctueux et profondément parfumé.',
        ingredients: [
          '1 L de lait entier',
          '300 g de sucre',
          '1 gousse de vanille fendue',
          '1 pincée de bicarbonate de soude',
        ],
        steps: [
          'Portez le lait, le sucre et la vanille à frémissement dans une casserole à fond épais.',
          'Ajoutez le bicarbonate hors du feu, puis remettez sur feu doux.',
          'Laissez réduire 2 heures en remuant régulièrement, jusqu’à obtenir une teinte ambrée.',
          'Retirez la vanille, laissez tiédir puis conservez au réfrigérateur jusqu’à 2 semaines.',
        ],
      },
      {
        id: 'alfajores-maison',
        title: 'Alfajores de Maïzena',
        time: '1 h 15',
        yield: '12 alfajores',
        description:
          'Des biscuits ultra fondants qui se défont sous la dent, garnis de dulce de leche.',
        ingredients: [
          '300 g de fécule de maïs',
          '150 g de farine',
          '200 g de beurre doux tempéré',
          '100 g de sucre glace',
          '3 jaunes d’œufs',
          'Le zeste d’un citron',
          'Dulce de leche et noix de coco râpée',
        ],
        steps: [
          'Crémez le beurre avec le sucre glace, puis incorporez les jaunes et le zeste.',
          'Ajoutez la fécule et la farine tamisées, sans trop travailler la pâte.',
          'Étalez à 1 cm, découpez des disques et réfrigérez 30 minutes.',
          'Cuisez 10 à 12 minutes à 170 °C : les biscuits doivent rester pâles.',
          'Garnissez généreusement de dulce de leche et roulez les bords dans la noix de coco.',
        ],
      },
      {
        id: 'ganache',
        title: 'Ganache Grand Cru 70 %',
        time: '20 min',
        yield: '20 truffes',
        description:
          'Trois ingrédients, une texture soyeuse. Peu de sucre, beaucoup de cacao.',
        ingredients: [
          '200 g de chocolat noir 70 %',
          '150 ml de crème 35 %',
          '30 g de beurre doux',
          'Cacao en poudre pour l’enrobage',
        ],
        steps: [
          'Hachez finement le chocolat et déposez-le dans un bol.',
          'Portez la crème à frémissement et versez-la en trois fois sur le chocolat.',
          'Mélangez du centre vers l’extérieur, puis incorporez le beurre.',
          'Réfrigérez 3 heures, façonnez des billes et roulez-les dans le cacao.',
        ],
      },
      {
        id: 'cakepops',
        title: 'Cake Pops au Chocolat',
        time: '1 h + repos',
        yield: '18 cake pops',
        description:
          'Des bouchées moelleuses enrobées de chocolat, à décorer selon vos envies.',
        ingredients: [
          '250 g de gâteau au chocolat émietté',
          '80 g de ganache ou de fromage à la crème',
          '300 g de chocolat de couverture',
          'Bâtonnets à cake pops',
          'Décorations : or alimentaire, perles, chocolat rosé',
        ],
        steps: [
          'Mélangez le gâteau émietté avec la ganache jusqu’à obtenir une pâte homogène.',
          'Façonnez des billes de 30 g et réfrigérez 1 heure.',
          'Trempez la pointe du bâtonnet dans le chocolat fondu, puis piquez chaque bille.',
          'Enrobez entièrement de chocolat tempéré et laissez l’excédent s’égoutter.',
          'Décorez immédiatement et laissez cristalliser à la verticale.',
        ],
      },
    ],
  },
  events: {
    eyebrow: 'Corporatif & Événements',
    titleLead: 'Des douceurs pour vos',
    titleAccent: 'grandes occasions',
    intro:
      'Nous réalisons des commandes sur mesure pour les entreprises et les événements : mariages, baptêmes, anniversaires, remerciements clients et cadeaux d’employés. Commandez directement par message privé.',
    offerings: [
      {
        title: 'Cadeaux corporatifs',
        text:
          'Coffrets élégants pour vos clients et vos équipes, avec possibilité de personnaliser l’emballage et d’y ajouter votre carte ou votre logo.',
      },
      {
        title: 'Mariages & célébrations',
        text:
          'Faveurs pour les invités, tables de douceurs, cake pops décorés et coffrets de remerciement assortis à votre palette.',
      },
      {
        title: 'Grandes quantités',
        text:
          'Alfajores, chocolats et cake pops en volume, avec un échéancier convenu à l’avance pour garantir une fraîcheur parfaite.',
      },
      {
        title: 'Saveurs personnalisées',
        text:
          'Nous développons avec vous des garnitures et des enrobages sur mesure pour créer une douceur unique à votre image.',
      },
    ],
    ctaTitle: 'Parlons de votre projet',
    ctaText:
      'Écrivez-nous en message privé ou par téléphone avec vos dates, vos quantités et vos idées. Prévoyez un délai minimum de 3 jours et une commande minimum de 50 $.',
    cta: 'Demander une soumission',
    dmCta: 'Commander par DM',
  },
  contact: {
    eyebrow: 'Nous Joindre',
    titleLead: 'Parlons',
    titleAccent: 'gourmandise',
    intro:
      'Une question, une commande spéciale, un projet d’événement ? Écrivez-nous, téléphonez-nous ou envoyez-nous un message privé.',
    pickupLabel: 'Cueillette',
    deliveryLabel: 'Livraison',
    phoneLabel: 'Téléphone',
    emailLabel: 'Courriel',
    ordersLabel: 'Commandes',
    ordersValue: 'Sur commande · 3 jours d’avance · Minimum 50 $',
    followTitle: 'Suivez-nous',
    form: {
      title: 'Envoyez-nous un message',
      name: 'Nom',
      email: 'Courriel',
      phone: 'Téléphone',
      message: 'Message',
      messagePlaceholder:
        'Dites-nous ce dont vous avez envie, la quantité et la date souhaitée…',
      send: 'Envoyer',
      note: 'Nous vous répondons généralement dans les 24 heures.',
    },
  },
  cart: {
    eyebrow: 'Votre commande',
    titleLead: 'Votre',
    titleAccent: 'Panier',
    empty: 'Votre panier est vide pour le moment.',
    emptyCta: 'Voir nos créations',
    quantity: 'Quantité',
    savedTitle: 'Gardés pour plus tard',
    savedEmpty: 'Aucun produit gardé pour le moment.',
    moveToCart: 'Ajouter au panier',
    remove: 'Retirer',
    subtotal: 'Sous-total',
    notice:
      'Tous nos produits sont faits sur commande : prévoyez un délai de 3 jours d’avance.',
    deliveryNote:
      'Cueillette à Chambly, ou livraison à Brossard, Longueuil, Candiac, Saint-Hubert, Saint-Jean-sur-Richelieu et Carignan — frais selon la distance.',
    minimumWarning:
      'Commande minimum de 50 $ — ajoutez encore {amount} $ pour finaliser.',
    minimumMet: 'Commande minimum atteinte.',
    checkout: 'Payer maintenant',
    securePayment:
      'Paiement sécurisé traité par Shopify. Vous serez redirigé vers la caisse pour régler par carte, Apple Pay ou Google Pay.',
    paymentMethodsLabel: 'Moyens de paiement acceptés',
    orderByDm: 'Commander par DM',
    continueShopping: 'Continuer mes achats',
    each: 'chacun',
  },
  refund: {
    title: 'Politique de remboursement',
    paragraphs: [
      'Tout changement d’avis concernant la commande entraîne la perte directe du produit.',
      'C’est pourquoi, une fois votre commande validée, il n’est pas possible de l’annuler ou de la retourner.',
    ],
  },
  footer: {
    tagline:
      'L’art du cacao, façonné à la main. Cueillette à Chambly ou livraison sur la Rive-Sud.',
    contactTitle: 'Nous Joindre',
    navigationTitle: 'Navigation',
    legalTitle: 'Informations',
    rights: 'Tous droits réservés.',
    refundLink: 'Politique de remboursement',
  },
}
